import {
  Children,
  forwardRef,
  isValidElement,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';

import { BaseDescriptionList } from '../base';
import { cn } from '../base/utils';

export interface WorkbenchDetailSidebarClassNames {
  root?: string;
  header?: string;
  body?: string;
  footer?: string;
  resizeHandle?: string;
  autoBody?: string;
  section?: string;
  sectionHeader?: string;
  sectionTitle?: string;
  sectionDescription?: string;
  descriptionList?: string;
  descriptionListItem?: string;
  descriptionListTerm?: string;
  descriptionListValue?: string;
  fieldLabel?: string;
  fieldLabelIcon?: string;
  fieldValue?: string;
  mediaValue?: string;
  mediaImage?: string;
  mediaCaption?: string;
  emptyState?: string;
}

export interface WorkbenchDetailSidebarBodyProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'className'> {
  className?: string;
}

export interface WorkbenchDetailSidebarField<Row = any> {
  key: string;
  label?: ReactNode;
  labelIcon?: ReactNode | ElementType;
  icon?: ReactNode | ElementType;
  renderType?: string;
  value?: ReactNode | ((record: Row | null | undefined, field: WorkbenchDetailSidebarField<Row>) => ReactNode);
  description?: ReactNode;
  hidden?: boolean | ((field: WorkbenchDetailSidebarField<Row>, record: Row | null | undefined) => boolean);
  emptyValue?: ReactNode;
  [key: string]: any;
}

export interface WorkbenchDetailSidebarSection<Row = any> {
  id?: string;
  title?: ReactNode;
  description?: ReactNode;
  fields?: WorkbenchDetailSidebarField<Row>[];
  columns?: 1 | 2 | 3;
  hidden?: boolean | ((section: WorkbenchDetailSidebarSection<Row>, record: Row | null | undefined) => boolean);
  emptyState?: ReactNode;
  [key: string]: any;
}

export interface WorkbenchDetailSidebarRenderContext<Row = any> {
  field: WorkbenchDetailSidebarField<Row>;
  fieldIndex: number;
  section: WorkbenchDetailSidebarSection<Row>;
  sectionIndex: number;
  record: Row | null | undefined;
}

export interface WorkbenchDetailSidebarProps<Row = any> extends Omit<ComponentPropsWithoutRef<'aside'>, 'children' | 'className' | 'style'> {
  as?: ElementType;
  bodyAs?: ElementType;
  open?: boolean;
  width?: number | string;
  header?: ReactNode;
  footer?: ReactNode;
  resizeHandle?: ReactNode;
  children?: ReactNode;
  record?: Row | null;
  fields?: WorkbenchDetailSidebarField<Row>[];
  sections?: WorkbenchDetailSidebarSection<Row>[];
  fieldColumns?: 1 | 2 | 3;
  emptyValue?: ReactNode;
  emptyState?: ReactNode;
  getFieldValue?: (
    field: WorkbenchDetailSidebarField<Row>,
    record: Row | null | undefined,
    context: WorkbenchDetailSidebarRenderContext<Row>,
  ) => unknown;
  renderFieldValue?: (
    field: WorkbenchDetailSidebarField<Row>,
    record: Row | null | undefined,
    context: WorkbenchDetailSidebarRenderContext<Row>,
  ) => ReactNode;
  formatFieldValue?: (
    value: unknown,
    context: WorkbenchDetailSidebarRenderContext<Row>,
  ) => ReactNode;
  formatFieldLabel?: (
    label: ReactNode,
    context: Omit<WorkbenchDetailSidebarRenderContext<Row>, 'fieldIndex'> & {
      field: WorkbenchDetailSidebarField<Row>;
      fieldIndex?: number;
    },
  ) => ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  resizeHandleClassName?: string;
  bodyProps?: WorkbenchDetailSidebarBodyProps;
  classNames?: WorkbenchDetailSidebarClassNames;
  style?: CSSProperties;
  [key: string]: any;
}

function shouldRenderBodyWrapper(props: {
  header?: ReactNode;
  footer?: ReactNode;
  bodyAs?: ElementType;
  bodyClassName?: string;
  bodyProps?: WorkbenchDetailSidebarBodyProps;
  classNames?: WorkbenchDetailSidebarClassNames;
}) {
  return Boolean(
    props.header ||
      props.footer ||
      props.bodyAs ||
      props.bodyClassName ||
      props.bodyProps ||
      props.classNames?.body,
  );
}

function hasRenderableChildren(children: ReactNode): boolean {
  if (children === null || children === undefined || typeof children === 'boolean') return false;
  if (typeof children === 'string') return children.trim().length > 0;
  if (Array.isArray(children)) return children.some(hasRenderableChildren);

  const childList = Children.toArray(children);
  if (!childList.length) return false;

  return childList.some((child) => (typeof child === 'string' ? child.trim().length > 0 : true));
}

function resolveWidthStyle(open: boolean | undefined, width: number | string | undefined) {
  if (width === undefined) return {};
  if (open === undefined) return { width };

  return {
    width: open ? width : 0,
    opacity: open ? 1 : 0,
  };
}

function getFallbackFieldLabel(field: WorkbenchDetailSidebarField) {
  return field.label ?? field.key;
}

function getObjectDisplayValue(value: Record<string, any>) {
  const displayKeys = ['text', 'label', 'title', 'display', 'value', 'recordId'];

  for (const key of displayKeys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) continue;

    const displayValue = value[key];
    if (displayValue !== null && displayValue !== undefined && displayValue !== '') {
      return displayValue;
    }
  }

  return undefined;
}

function isEmptyDetailValue(value: unknown) {
  return value === null || value === undefined || value === '';
}

function isPlainDisplayValue(value: ReactNode) {
  return ['string', 'number', 'boolean'].includes(typeof value);
}

function getMediaDisplayData(value: unknown) {
  if (!value) return null;

  if (typeof value === 'string') {
    const isDataImage = value.startsWith('data:image/');
    const isImageUrl = /\.(avif|gif|jpe?g|png|svg|webp)(\?.*)?$/i.test(value);
    return isDataImage || isImageUrl ? { src: value } : null;
  }

  if (typeof value !== 'object' || isValidElement(value)) return null;

  const media = value as Record<string, any>;
  const src = media.src || media.url || media.thumbnail || media.preview || media.image;

  return src
    ? {
      src: String(src),
      alt: media.alt || media.label || media.title || '',
      caption: media.caption || media.description || media.label || media.title,
    }
    : null;
}

function shouldRenderAsMedia(field: WorkbenchDetailSidebarField | undefined, value: unknown) {
  const renderType = String(field?.renderType || '').toLowerCase();
  return ['image', 'media', 'preview', 'thumbnail'].includes(renderType) || Boolean(getMediaDisplayData(value));
}

function renderMaybeFieldIcon(icon: ReactNode | ElementType | undefined, className?: string) {
  if (!icon) {
    return (
      <span className={cn('relative inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center', className)}>
        <span className="absolute inset-0 rounded-full border border-primary/25 bg-primary/10" />
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
      </span>
    );
  }

  if (typeof icon === 'string' || typeof icon === 'number') {
    return (
      <span className={cn('inline-flex h-3.5 min-w-3.5 shrink-0 items-center justify-center rounded-sm text-[10px] font-semibold text-primary', className)}>
        {icon}
      </span>
    );
  }

  if (typeof icon === 'boolean') return null;

  if (isValidElement(icon)) return icon;

  const IconComponent = icon as ElementType;
  return <IconComponent className={cn('h-3.5 w-3.5 shrink-0 text-primary', className)} />;
}

function renderDetailFieldLabel<Row>(
  label: ReactNode,
  field: WorkbenchDetailSidebarField<Row>,
  classNames?: WorkbenchDetailSidebarClassNames,
) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5 text-[11px] font-semibold text-foreground', classNames?.fieldLabel)}>
      {renderMaybeFieldIcon(field.labelIcon || field.icon, classNames?.fieldLabelIcon)}
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}

function WorkbenchDetailSidebarMediaValue({
  media,
  classNames,
}: {
  media: NonNullable<ReturnType<typeof getMediaDisplayData>>;
  classNames?: WorkbenchDetailSidebarClassNames;
}) {
  return (
    <div className={cn('grid gap-2', classNames?.mediaValue)}>
      <div className="overflow-hidden rounded-md border border-border/55 bg-muted/20 dark:border-border/45 dark:bg-muted/10">
        <img
          src={media.src}
          alt={String(media.alt || '')}
          className={cn('block aspect-[16/9] w-full object-cover', classNames?.mediaImage)}
        />
      </div>
      {media.caption ? (
        <div className={cn('truncate text-[11px] leading-4 text-muted-foreground', classNames?.mediaCaption)}>
          {media.caption}
        </div>
      ) : null}
    </div>
  );
}

function getDefaultDetailDisplayValue(
  value: unknown,
  emptyValue: ReactNode,
  field?: WorkbenchDetailSidebarField,
  classNames?: WorkbenchDetailSidebarClassNames,
): ReactNode {
  if (isEmptyDetailValue(value)) return emptyValue;
  if (isValidElement(value)) return value;
  if (shouldRenderAsMedia(field, value)) {
    const media = getMediaDisplayData(value);
    if (media) return <WorkbenchDetailSidebarMediaValue media={media} classNames={classNames} />;
  }
  if (Array.isArray(value)) {
    const parts: ReactNode[] = value
      .map((item) => getDefaultDetailDisplayValue(item, '', undefined, classNames))
      .filter((item) => !isEmptyDetailValue(item));

    if (!parts.length) return emptyValue;
    if (parts.every(isPlainDisplayValue)) return parts.map(String).join(' · ');

    return (
      <span className="inline-flex flex-wrap items-center gap-1">
        {parts.map((part, index) => (
          <span key={index} className="inline-flex items-center gap-1">
            {index > 0 ? <span className="text-muted-foreground">·</span> : null}
            {isPlainDisplayValue(part) ? String(part) : part}
          </span>
        ))}
      </span>
    );
  }
  if (typeof value === 'object') {
    const displayValue = getObjectDisplayValue(value as Record<string, any>);
    return displayValue === undefined
      ? emptyValue
      : getDefaultDetailDisplayValue(displayValue, emptyValue, field, classNames);
  }
  return String(value);
}

function getDefaultFieldValue<Row>(
  field: WorkbenchDetailSidebarField<Row>,
  record: Row | null | undefined,
) {
  if (Object.prototype.hasOwnProperty.call(field, 'value')) {
    return typeof field.value === 'function'
      ? field.value(record, field)
      : field.value;
  }

  return record ? (record as any)?.[field.key] : undefined;
}

function shouldHideField<Row>(
  field: WorkbenchDetailSidebarField<Row>,
  record: Row | null | undefined,
) {
  return typeof field.hidden === 'function'
    ? field.hidden(field, record)
    : Boolean(field.hidden);
}

function shouldHideSection<Row>(
  section: WorkbenchDetailSidebarSection<Row>,
  record: Row | null | undefined,
) {
  return typeof section.hidden === 'function'
    ? section.hidden(section, record)
    : Boolean(section.hidden);
}

function WorkbenchDetailSidebarAutoContent<Row = any>({
  record,
  fields,
  sections,
  fieldColumns = 1,
  emptyValue = '—',
  emptyState,
  getFieldValue,
  renderFieldValue,
  formatFieldValue,
  formatFieldLabel,
  classNames,
}: Pick<
  WorkbenchDetailSidebarProps<Row>,
  | 'record'
  | 'fields'
  | 'sections'
  | 'fieldColumns'
  | 'emptyValue'
  | 'emptyState'
  | 'getFieldValue'
  | 'renderFieldValue'
  | 'formatFieldValue'
  | 'formatFieldLabel'
  | 'classNames'
>) {
  const resolvedSections = sections?.length
    ? sections
    : fields?.length
      ? [{ fields, columns: fieldColumns }]
      : [];
  const visibleSections = resolvedSections.filter((section) => !shouldHideSection(section, record));

  if (!visibleSections.length) {
    return emptyState ? (
      <div className={cn('rounded-lg border border-dashed border-border/60 px-3 py-6 text-center text-xs text-muted-foreground', classNames?.emptyState)}>
        {emptyState}
      </div>
    ) : null;
  }

  return (
    <div className={cn('space-y-3', classNames?.autoBody)}>
      {visibleSections.map((section, sectionIndex) => {
        const visibleFields = (section.fields || []).filter((field) => !shouldHideField(field, record));
        const sectionKey = section.id || `section-${sectionIndex}`;

        if (!visibleFields.length && !section.emptyState) return null;

        const items = visibleFields.map((field, fieldIndex) => {
          const context: WorkbenchDetailSidebarRenderContext<Row> = {
            field,
            fieldIndex,
            section,
            sectionIndex,
            record,
          };
          const rawValue = getFieldValue
            ? getFieldValue(field, record, context)
            : getDefaultFieldValue(field, record);
          const value = renderFieldValue
            ? renderFieldValue(field, record, context)
            : formatFieldValue
              ? formatFieldValue(rawValue, context)
              : getDefaultDetailDisplayValue(rawValue, field.emptyValue ?? emptyValue, field, classNames);
          const fieldLabel = formatFieldLabel
            ? formatFieldLabel(getFallbackFieldLabel(field), context)
            : getFallbackFieldLabel(field);

          return {
            term: renderDetailFieldLabel(fieldLabel, field, classNames),
            value,
          };
        });

        return (
          <section key={sectionKey} className={cn('min-w-0 space-y-3', classNames?.section)}>
            {section.title || section.description ? (
              <div className={cn('min-w-0 space-y-1.5', classNames?.sectionHeader)}>
                {section.title ? (
                  <div className={cn('inline-flex max-w-full items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-2.5 py-1.5 text-xs font-medium text-primary dark:border-primary/25 dark:bg-primary/10', classNames?.sectionTitle)}>
                    {section.title}
                  </div>
                ) : null}
                {section.description ? (
                  <div className={cn('text-[11px] leading-5 text-muted-foreground', classNames?.sectionDescription)}>
                    {section.description}
                  </div>
                ) : null}
              </div>
            ) : null}
            {items.length ? (
              <BaseDescriptionList
                items={items}
                columns={section.columns || fieldColumns}
                className={cn('gap-3', classNames?.descriptionList)}
                classNames={{
                  item: cn('!border-0 !bg-transparent !p-0 !shadow-none !gap-1.5', classNames?.descriptionListItem),
                  term: cn('!normal-case !tracking-normal !text-foreground', classNames?.descriptionListTerm),
                  value: cn(
                    'min-h-10 rounded-md border border-border/55 bg-muted/25 px-3 py-2 text-xs leading-5 text-foreground shadow-[inset_0_1px_0_hsl(var(--background)/0.8)] dark:border-border/45 dark:bg-white/[0.035]',
                    classNames?.fieldValue,
                    classNames?.descriptionListValue,
                  ),
                }}
              />
            ) : (
              <div className={cn('py-3 text-center text-xs text-muted-foreground', classNames?.emptyState)}>
                {section.emptyState}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export const WorkbenchDetailSidebar = forwardRef<HTMLElement, WorkbenchDetailSidebarProps>(
  function WorkbenchDetailSidebar(
    {
      as: Component = 'aside',
      bodyAs: BodyComponent = 'div',
      open,
      width,
      header,
      footer,
      resizeHandle,
      children,
      record,
      fields,
      sections,
      fieldColumns,
      emptyValue,
      emptyState,
      getFieldValue,
      renderFieldValue,
      formatFieldValue,
      formatFieldLabel,
      className,
      headerClassName,
      bodyClassName,
      footerClassName,
      resizeHandleClassName,
      bodyProps,
      classNames,
      style,
      ...props
    },
    ref,
  ) {
    const autoContent = hasRenderableChildren(children) ? children : (
      <WorkbenchDetailSidebarAutoContent
        record={record}
        fields={fields}
        sections={sections}
        fieldColumns={fieldColumns}
        emptyValue={emptyValue}
        emptyState={emptyState}
        getFieldValue={getFieldValue}
        renderFieldValue={renderFieldValue}
        formatFieldValue={formatFieldValue}
        formatFieldLabel={formatFieldLabel}
        classNames={classNames}
      />
    );
    const renderBodyWrapper = shouldRenderBodyWrapper({
      header,
      footer,
      bodyAs: BodyComponent === 'div' ? undefined : BodyComponent,
      bodyClassName,
      bodyProps,
      classNames,
    });
    const { className: bodyPropsClassName, ...restBodyProps } = bodyProps ?? {};

    return (
      <Component
        ref={ref}
        data-workbench-detail-sidebar
        data-open={open === undefined ? undefined : open ? 'true' : 'false'}
        className={cn(
          'workbench-surface-subtle relative flex flex-shrink-0 flex-col self-stretch overflow-hidden rounded-lg border border-border/50 transition-all duration-300 dark:border-border/40',
          open === false && 'pointer-events-none',
          classNames?.root,
          className,
        )}
        style={{
          ...resolveWidthStyle(open, width),
          ...style,
        }}
        {...props}
      >
        {resizeHandle ? (
          <div className={cn(classNames?.resizeHandle, resizeHandleClassName)}>
            {resizeHandle}
          </div>
        ) : null}
        {header ? (
          <div className={cn('shrink-0 border-b border-border/40 px-4 py-3 dark:border-border/30', classNames?.header, headerClassName)}>
            {header}
          </div>
        ) : null}
        {renderBodyWrapper ? (
          <BodyComponent
            className={cn('min-h-0 flex-1 overflow-y-auto', classNames?.body, bodyClassName, bodyPropsClassName)}
            {...restBodyProps}
          >
            {autoContent}
          </BodyComponent>
        ) : (
          autoContent
        )}
        {footer ? (
          <div className={cn('shrink-0 border-t border-border/40 px-4 py-3 dark:border-border/30', classNames?.footer, footerClassName)}>
            {footer}
          </div>
        ) : null}
      </Component>
    );
  },
);
